/**
 * Inbox (`/inbox`) — Admin only.
 *
 * Pulls /api/visibility/inbox. Two sections:
 *   - Pending parts approvals (action: review)
 *   - Overdue projects (action: investigate)
 *
 * Unread notification count appears as a status hint in the page header.
 */
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { AlertTriangle, CheckCircle2, Clock, FileWarning, Inbox } from 'lucide-react';
import { formatRelative } from '@procurement/shared';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorPanel } from '@/components/ui/error-panel';
import { PageHeader } from '@/components/ui/page-header';
import { ProjectNumber } from '@/components/ui/project-number';
import { Spinner } from '@/components/ui/spinner';
import { StatusBadge } from '@/components/ui/status-badge';
import { useCurrentUser } from '@/lib/auth';
import { useInbox } from '@/lib/visibility-queries';
import { usePageTitle } from '@/lib/use-page-title';

export function InboxPage() {
  usePageTitle('Inbox');
  const me = useCurrentUser();
  const navigate = useNavigate();
  const isAdmin = me.data?.user.role === 'ADMIN';

  // Assistants shouldn't reach /inbox via the nav, but if they enter via URL
  // bounce them to the dashboard rather than show an empty/forbidden screen.
  useEffect(() => {
    if (me.data && !isAdmin) void navigate({ to: '/', replace: true });
  }, [isAdmin, me.data, navigate]);

  const inbox = useInbox({ enabled: isAdmin });

  if (!isAdmin) return null;

  if (inbox.isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }
  // Patch 5 — explicit error fallback
  if (inbox.isError || !inbox.data) {
    return (
      <>
        <PageHeader title="Inbox" />
        <ErrorPanel onRetry={() => inbox.refetch()} />
      </>
    );
  }

  const pending = inbox.data?.pendingPartsApprovals ?? [];
  const overdue = inbox.data?.overdueProjects ?? [];
  const unread = inbox.data?.unreadNotificationCount ?? 0;

  return (
    <>
      <PageHeader
        title="Inbox"
        subtitle={
          unread > 0
            ? `${unread} unread ${unread === 1 ? 'notification' : 'notifications'}`
            : 'All caught up on notifications.'
        }
      />

      <div className="space-y-6">
        <Card
          title={`Pending parts approvals${pending.length > 0 ? ` · ${pending.length}` : ''}`}
          bodyClassName="p-0"
        >
          {pending.length === 0 ? (
            <EmptyState
              icon={<CheckCircle2 className="h-6 w-6 text-status-success" />}
              title="Inbox zero"
              body="No parts approvals waiting for your review."
            />
          ) : (
            <ul className="divide-y divide-border-default">
              {pending.map((sub) => (
                <li key={sub.projectId}>
                  <Link
                    to="/projects/$projectId"
                    params={{ projectId: sub.projectId }}
                    className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-subtle"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <ProjectNumber number={sub.projectNumber} size="sm" />
                        <span className="truncate font-medium text-primary">
                          {sub.projectTitle}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-secondary">
                        <Clock className="h-3 w-3" />
                        Submitted by {sub.submittedByDisplayName} ·{' '}
                        {formatRelative(sub.submittedAt)}
                      </div>
                    </div>
                    <StatusBadge kind="partsWorkflow" value="PENDING_APPROVAL" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          title={`Overdue & blocked${overdue.length > 0 ? ` · ${overdue.length}` : ''}`}
          bodyClassName="p-0"
        >
          {overdue.length === 0 ? (
            <EmptyState
              icon={<Inbox className="h-6 w-6" />}
              title="Nothing overdue"
              body="No projects with overdue batches or unresolved inspections."
            />
          ) : (
            <ul className="divide-y divide-border-default">
              {overdue.map((p) => (
                <li key={p.projectId}>
                  <Link
                    to="/projects/$projectId"
                    params={{ projectId: p.projectId }}
                    className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-subtle"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <ProjectNumber number={p.projectNumber} size="sm" />
                        <span className="truncate font-medium text-primary">{p.projectTitle}</span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-3 text-xs text-secondary">
                        {p.overdueBatchCount > 0 ? (
                          <span className="inline-flex items-center gap-1 text-status-danger">
                            <AlertTriangle className="h-3 w-3" />
                            {p.overdueBatchCount} overdue{' '}
                            {p.overdueBatchCount === 1 ? 'batch' : 'batches'}
                          </span>
                        ) : null}
                        {p.unresolvedInspectionCount > 0 ? (
                          <span className="inline-flex items-center gap-1 text-status-warning">
                            <FileWarning className="h-3 w-3" />
                            {p.unresolvedInspectionCount} unresolved inspection
                            {p.unresolvedInspectionCount === 1 ? '' : 's'}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <StatusBadge kind="lifecycle" value={p.lifecycleState} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
